import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery,
  Paper,
  InputAdornment,
  Badge
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  ArrowRight as ArrowRightIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { getSyncLogs, getSyncLogWithData } from '../services/api';
import ServerDataTable from '../components/ServerDataTable/ServerDataTable';
import PageHeader from '../components/PageHeader';
import JsonDataDisplay from '../components/common/JsonDataDisplay';
import StatusBadge from '../components/StatusBadge';
import SyncItemsSkeleton from '../components/ServerDataTable/SyncItemsSkeleton';
import TablePaginationForSyncedDataComponent from '../components/ServerDataTable/TablePaginationForSyncedData';
import { formatDate } from '../utils/formatDate';
import { useLocation, useNavigate } from 'react-router-dom';

// Constants
const DEBOUNCE_DELAY = 500;
const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_ITEMS_PER_PAGE = 10;

// Initial states
const initialFilters = {
  syncType: '',
  direction: '',
  status: '',
  initiatedBy: '',
  startDate: '',
  endDate: ''
};

const initialTotalCounts = {
  all: 0,
  success: 0,
  partially_success: 0,
  failed: 0,
  created: 0,
  updated: 0,
  deleted: 0
};

const formatDuration = (durationMs) => {
  if (!durationMs) return 'N/A';
  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  const remainingSeconds = seconds % 60;

  if (hours > 0) return `${hours}h ${remainingMinutes}m ${remainingSeconds}s`;
  if (minutes > 0) return `${minutes}m ${remainingSeconds}s`;
  return `${seconds}s`;
};

// Helper function to get chip color based on filter type
const getChipColor = (filter) => {
  switch (filter) {
  case 'success':
    return 'success';
  case 'partially_success':
    return 'warning';
  case 'failed':
    return 'error';
  case 'created':
    return 'info';
  case 'updated':
    return 'secondary';
  case 'deleted':
    return 'error';
  default:
    return 'primary';
  }
};

// Helper to count active filters
const getActiveFilterCount = (filters) => {
  return Object.values(filters).filter(v => v !== '').length;
};

export default function SyncLogs() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();

  // Table states
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [filters, setFilters] = useState(initialFilters);
  const [showFilters, setShowFilters] = useState(false);
  const [modalFilters, setModalFilters] = useState(initialFilters);

  // On mount, check for query params and set initial filters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const newFilters = { ...initialFilters };

    // Load all filter parameters from URL
    const filterKeys = ['syncType', 'direction', 'status', 'initiatedBy', 'startDate', 'endDate'];
    let hasFilters = false;

    filterKeys.forEach(key => {
      const value = params.get(key);
      if (value) {
        newFilters[key] = value;
        hasFilters = true;
      }
    });

    // Load pagination parameters from URL
    const pageParam = params.get('page');
    const pageSizeParam = params.get('pageSize');

    // Update page index from URL
    if (pageParam !== null) {
      // Convert 1-indexed URL to 0-indexed internal state
      const newPage = parseInt(pageParam, 10) - 1;
      setPage(newPage);
    } else {
      // If no page parameter, reset to first page
      setPage(0);
    }

    if (pageSizeParam !== null) {
      setPageSize(parseInt(pageSizeParam, 10));
    }

    if (hasFilters) {
      setFilters(newFilters);
      setModalFilters(newFilters);
    }
  }, [location.search]);

  // Additional effect to ensure URL state is properly reflected in component state
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pageParam = params.get('page');

    if (pageParam !== null) {
      const urlPage = parseInt(pageParam, 10) - 1;
      // Only update if the current page doesn't match the URL
      if (page !== urlPage) {
        setPage(urlPage);
      }
    }
  }, [location.search, page]);

  // Dialog states
  const [selectedLog, setSelectedLog] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDialogMaximized, setIsDialogMaximized] = useState(true);
  const [detailedData, setDetailedData] = useState(null);

  // Sync items states
  const [itemsPage, setItemsPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [totalItems, setTotalItems] = useState(0);
  const [loadingPagination, setLoadingPagination] = useState(false);
  const [currentFilter, setCurrentFilter] = useState('all');
  const [totalCounts, setTotalCounts] = useState(initialTotalCounts);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(itemSearchTerm);
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [itemSearchTerm]);

  // Fetch data when debounced search term changes
  useEffect(() => {
    if (selectedLog && detailedData !== null) {
      fetchDetailedData(selectedLog.id, itemsPage, itemsPerPage, currentFilter);
    }
  }, [debouncedSearchTerm]);

  // Fetch sync logs
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['syncLogs', page, pageSize, filters],
    queryFn: async () => {
      try {
        const params = {
          limit: pageSize,
          offset: page * pageSize,
          ...filters
        };

        // Remove empty filters
        Object.keys(params).forEach((key) => {
          if (params[key] === '') delete params[key];
        });

        const response = await getSyncLogs(params);
        return {
          logs: response.data?.data?.logs || [],
          pagination: response.data?.data?.pagination || { total: 0 }
        };
      } catch (error) {
        console.error('Error fetching sync logs:', error);
        return { logs: [], pagination: { total: 0 } };
      }
    },
    staleTime: 0
  });

  // Calculate total counts from sync data
  const calculateTotalCounts = useCallback((data) => {
    if (!data?.syncDataItems) return initialTotalCounts;

    return data.syncDataItems.reduce((counts, item) => {
      // Parse item if it's a string
      const itemData = typeof item === 'string' ? JSON.parse(item) : item;
      counts.all++;

      // Count status
      if (itemData.status === 'success') counts.success++;
      else if (itemData.status === 'partially_success') counts.partially_success++;
      else if (itemData.status === 'failed') counts.failed++;

      // Count actions
      if (itemData.action === 'created') counts.created++;
      else if (itemData.action === 'updated') counts.updated++;
      else if (itemData.action === 'deleted' ||
        itemData.is_marked_as_deleted_in_shopify ||
        itemData.is_unpublished_in_shopify) {
        counts.deleted++;
      }

      return counts;
    }, { ...initialTotalCounts });
  }, []);

  // Fetch detailed sync data
  const fetchDetailedData = useCallback(async (logId, page = 0, pageSize = DEFAULT_ITEMS_PER_PAGE, filter = 'all') => {
    setLoadingPagination(true);
    try {
      const response = await getSyncLogWithData(logId, {
        limit: pageSize,
        offset: page * pageSize,
        filter,
        search: debouncedSearchTerm,
        // Apply deleted filter for customer sync from ERP to Shopify
        showDeleted: (selectedLog?.sync_type === 'customer' && selectedLog?.direction === 'erp_to_shopify' && filter === 'deleted') ||
          (selectedLog?.sync_type === 'product' && selectedLog?.direction === 'erp_to_shopify' && filter === 'deleted') ||
          (selectedLog?.sync_type === 'price' && selectedLog?.direction === 'erp_to_shopify' && filter === 'deleted')
      });

      const data = response.data?.data;
      if (data) {
        setDetailedData(data);
        setTotalItems(data.syncDataCount || 0);

        // Use backend provided counts - they now include deleted count
        const counts = data.totalCounts || calculateTotalCounts(data);
        setTotalCounts(counts);
      }
    } catch (error) {
      console.error('Error fetching detailed sync data:', error);
    } finally {
      setLoadingPagination(false);
    }
  }, [debouncedSearchTerm, calculateTotalCounts, selectedLog]);

  // Handle item search
  const handleItemSearch = (event) => {
    setItemSearchTerm(event.target.value);
    setItemsPage(0); // Reset to first page when search changes
  };

  // Function to update URL with filter and pagination parameters
  const updateURLWithFilters = (newFilters, currentPage = page, currentPageSize = pageSize) => {
    const params = new URLSearchParams();

    // Add non-empty filter values to URL
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value && value !== '') {
        params.set(key, value);
      }
    });

    // Add pagination parameters to URL
    if (currentPage >= 0) {
      // Convert 0-indexed internal state to 1-indexed URL
      params.set('page', (currentPage + 1).toString());
    }

    params.set('pageSize', currentPageSize.toString() || DEFAULT_PAGE_SIZE.toString());

    // Update URL without reloading the page
    const newURL = params.toString() ? `?${params.toString()}` : '';
    navigate(`/sync-logs${newURL}`, { replace: true });
  };

  // Handle filter changes
  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setModalFilters(prev => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    setFilters(modalFilters);
    setPage(0);
    setShowFilters(false);
    updateURLWithFilters(modalFilters, 0, pageSize);
    refetch();
  };

  const resetFilters = () => {
    setModalFilters(initialFilters);
    setFilters(initialFilters);
    setPage(0);
    setShowFilters(false);
    updateURLWithFilters(initialFilters, 0, pageSize);
    refetch();
  };

  // Check if any applied filters have values (for Clear Filters button)
  const hasAppliedFilters = () => {
    return Object.values(filters).some(value => value !== '');
  };

  // Check if modal filters have changed from applied filters (for Apply Filters button)
  const hasFilterFormChanges = () => {
    return Object.keys(modalFilters).some(key => modalFilters[key] !== filters[key]);
  };

  // Sync modal filters when URL changes
  useEffect(() => {
    if (showFilters) {
      setModalFilters(filters);
    }
  }, [filters, showFilters]);

  // Handle dialog
  const viewLogDetails = useCallback(async (log) => {
    setSelectedLog(log);
    setCurrentFilter('all');
    setIsDialogOpen(true);
    setItemsPage(0);
    setItemSearchTerm('');
    setDetailedData(null);
    setTotalCounts(initialTotalCounts);
    await fetchDetailedData(log.id, 0, itemsPerPage, 'all', true);
  }, [fetchDetailedData, itemsPerPage]);

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setCurrentFilter('all');
    setIsDialogMaximized(true);
    setDetailedData(null);
    setLoadingPagination(false);
    setItemsPage(0);
    setTotalItems(0);
    setItemSearchTerm('');
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setPage(newPage);
    updateURLWithFilters(filters, newPage, pageSize);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0);
    updateURLWithFilters(filters, 0, newPageSize);
  };

  const toggleDialogSize = () => {
    setIsDialogMaximized(!isDialogMaximized);
  };

  // Handle search submit
  const handleSearchSubmit = (event) => {
    event.preventDefault();
    if (selectedLog && detailedData !== null) {
      fetchDetailedData(selectedLog.id, 0, itemsPerPage, currentFilter);
    }
  };

  // Handle items pagination
  const handleItemsPageChange = (event, newPage) => {
    // Handle both signatures: (event, newPage) and (newPage)
    const page = typeof event === 'number' ? event : newPage;
    setItemsPage(page);
    if (selectedLog) {
      fetchDetailedData(selectedLog.id, page, itemsPerPage, currentFilter);
    }
  };

  const handleItemsPerPageChange = (newSize) => {
    setItemsPerPage(newSize);
    setItemsPage(0);
    if (selectedLog) {
      fetchDetailedData(selectedLog.id, 0, newSize, currentFilter);
    }
  };

  // Table columns configuration
  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 80,
      sortable: false,
      align: 'left'
    },
    {
      field: '',
      headerName: 'Type',
      width: 150,
      sortable: false,
      renderCell: ({ row }) => {
        const type = row.sync_type || 'N/A';
        const subType = row.sync_sub_type ? ` (${row.sync_sub_type})` : '';
        return `${type}${subType}`;
      }
    },
    {
      field: 'direction',
      headerName: 'Direction',
      width: 150,
      sortable: false,
      renderCell: ({ value }) => value?.split('_').join(' ').toUpperCase() || 'N/A'
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      sortable: false,
      align: 'left',
      renderCell: ({ value }) => (
        <StatusBadge status={value} />
      )
    },
    {
      field: 'created_at',
      headerName: 'Timestamp',
      width: 200,
      sortable: false,
      renderCell: ({ value }) => formatDate(value)
    },
    {
      field: 'duration_ms',
      headerName: 'Duration',
      width: 120,
      sortable: false,
      renderCell: ({ value }) => formatDuration(value)
    },
    {
      field: '',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      align: 'center',
      renderCell: ({ row }) => (
        <Tooltip title='View Details'>
          <IconButton size='small' onClick={() => viewLogDetails(row)}>
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
      )
    }
  ];

  // Render filter chips
  const renderFilterChips = () => {
    // Get available filters based on sync type and direction
    const getAvailableFilters = () => {
      const baseFilters = ['all', 'success', 'partially_success', 'failed', 'created', 'updated'];

      // Add deleted filter for customer sync from ERP to Shopify
      if (selectedLog?.sync_type === 'customer' && selectedLog?.direction === 'erp_to_shopify') {
        baseFilters.push('deleted');
      }

      // Add deleted filter for product sync from ERP to Shopify
      if (selectedLog?.sync_type === 'product' && selectedLog?.direction === 'erp_to_shopify') {
        baseFilters.push('deleted');
      }
      // --- Add deleted filter for price sync from ERP to Shopify ---
      if (selectedLog?.sync_type === 'price' && selectedLog?.direction === 'erp_to_shopify') {
        baseFilters.push('deleted');
      }

      return baseFilters;
    };

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant='subtitle2' color='text.secondary' sx={{ mb: 1.5 }}>Filter By Status</Typography>
        <Grid container spacing={2}>
          {getAvailableFilters().map((filter) => (
            <Grid item key={filter}>
              <Chip
                label={`${filter.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')} (${totalCounts[filter] || 0})`}
                color={currentFilter === filter ? getChipColor(filter) : 'default'}
                variant={currentFilter === filter ? 'filled' : 'outlined'}
                size='small'
                onClick={async () => {
                  setCurrentFilter(filter);
                  setItemsPage(0);
                  setItemSearchTerm('');
                  if (selectedLog) {
                    await fetchDetailedData(selectedLog.id, 0, itemsPerPage, filter);
                  }
                }}
                sx={{
                  cursor: 'pointer',
                  fontWeight: currentFilter === filter ? 500 : 400,
                  borderRadius: 1.5,
                  '&:hover': {
                    backgroundColor: currentFilter === filter ? undefined : 'action.hover'
                  }
                }}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  // Render sync items
  const renderSyncItems = () => {
    if (loadingPagination) {
      return <SyncItemsSkeleton count={6} />;
    }

    const items = detailedData?.syncDataItems || [];
    if (items.length === 0) {
      return (
        <Paper
          variant='outlined'
          sx={{
            p: 3,
            textAlign: 'center',
            borderRadius: 2,
            backgroundColor: 'background.default'
          }}
        >
          <Typography variant='body2' color='text.secondary'>
            {currentFilter === 'all'
              ? 'No sync data available for this log'
              : `No sync data available for this log for '${currentFilter === 'partially_success' ? 'Partially Success' : currentFilter.charAt(0).toUpperCase() + currentFilter.slice(1)}' filter`
            }
          </Typography>
        </Paper>
      );
    }

    return (
      <>
        {items.map((item, index) => (
          <Accordion
            key={index}
            sx={{
              mb: 1,
              boxShadow: 'none',
              '&:before': { display: 'none' },
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '8px !important',
              overflow: 'hidden',
              '&:not(:last-child)': {
                borderBottom: '1px solid',
                borderColor: 'divider'
              },
              '& .MuiAccordionSummary-root': {
                borderRadius: '8px',
                transition: 'background-color 0.2s ease',
                '&:hover': {
                  backgroundColor: 'action.hover'
                },
                py: 1
              },
              '& .MuiAccordionDetails-root': {
                borderTop: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.default',
                p: 2
              }
            }}
          >
            <AccordionSummary
              expandIcon={<ArrowRightIcon />}
              sx={{
                py: 1,
                '& .MuiAccordionSummary-content': {
                  margin: '12px 0'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant='body2'>
                  {selectedLog?.sync_type === 'price'
                    ? item.item?.rule_name || `Rule ${itemsPage * itemsPerPage + index + 1}`
                    : item.item?.item_item || `Item ${itemsPage * itemsPerPage + index + 1}`}
                </Typography>
                {item.status && (
                  <StatusBadge status={item.status} />
                )}
                {item.action && (
                  <Chip
                    label={item.action}
                    color={item.action === 'deleted' ? 'error' : 'info'}
                    size='small'
                    variant='outlined'
                    sx={{ borderRadius: 1 }}
                  />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ maxHeight: '300px', overflow: 'auto' }}>
              <JsonDataDisplay
                data={item}
                sx={{ mb: 0 }}
              />
            </AccordionDetails>
          </Accordion>
        ))}

        <Box sx={{ mt: 2 }}>
          <TablePaginationForSyncedDataComponent
            currentPage={itemsPage}
            rowsPerPage={itemsPerPage}
            onPageChange={handleItemsPageChange}
            onRowsPerPageChange={handleItemsPerPageChange}
            totalRows={totalItems}
            totalPages={Math.ceil(totalItems / itemsPerPage)}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Box>
      </>
    );
  };

  // Render dialog content
  const renderDialogContent = () => {
    if (!selectedLog) return null;

    // Handle customer webhook creation logs
    if (
      selectedLog.sync_type === 'customer_webhook' &&
      selectedLog.sync_sub_type === 'customer_creation' &&
      selectedLog.direction === 'shopify_to_erp'
    ) {
      try {
        const details = JSON.parse(selectedLog.details || '{}');
        const { steps, start_time, end_time, duration_ms } = details;

        return (
          <Box>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <Card sx={{
                  boxShadow: 'none',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  height: '100%'
                }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant='subtitle2' color='text.secondary' gutterBottom>Type</Typography>
                    <Typography variant='body1' sx={{ mt: 1 }}>
                      {selectedLog.sync_type} {selectedLog.sync_sub_type && `(${selectedLog.sync_sub_type})`}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{
                  boxShadow: 'none',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  height: '100%'
                }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant='subtitle2' color='text.secondary' gutterBottom>Direction</Typography>
                    <Typography variant='body1' sx={{ mt: 1 }}>
                      {selectedLog.direction?.split('_').join(' ').toUpperCase()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{
                  boxShadow: 'none',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  height: '100%'
                }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant='subtitle2' color='text.secondary' gutterBottom>Status</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <StatusBadge status={selectedLog.status} size='large' />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{
                  boxShadow: 'none',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  height: '100%'
                }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant='subtitle2' color='text.secondary' gutterBottom>Start Time</Typography>
                    <Typography variant='body1' sx={{ mt: 1 }}>
                      {formatDate(start_time)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{
                  boxShadow: 'none',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  height: '100%'
                }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant='subtitle2' color='text.secondary' gutterBottom>End Time</Typography>
                    <Typography variant='body1' sx={{ mt: 1 }}>
                      {formatDate(end_time)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{
                  boxShadow: 'none',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  height: '100%'
                }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant='subtitle2' color='text.secondary' gutterBottom>Duration</Typography>
                    <Typography variant='body1' sx={{ mt: 1 }}>
                      {formatDuration(duration_ms)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Typography variant='subtitle2' sx={{ mt: 2, mb: 1 }}>
              Process Steps
            </Typography>
            {steps && steps.length ? (
              steps.map((step, index) => {
                const stepKey = Object.keys(step)[0];
                const stepData = step[stepKey];
                const {
                  step_name,
                  status,
                  message,
                  identifiers,
                  timestamp
                } = stepData;

                return (
                  <Accordion key={index} sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ArrowRightIcon />}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%'
                        }}
                      >
                        <Typography sx={{ flex: 1 }}>
                          {step_name}
                        </Typography>
                        <StatusBadge status={status} />
                        <Typography
                          variant='caption'
                          sx={{ ml: 2, color: 'text.secondary' }}
                        >
                          {formatDate(timestamp)}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant='subtitle2' color='text.secondary'>
                            Message
                          </Typography>
                          <Typography variant='body2' sx={{ mb: 2 }}>
                            {message}
                          </Typography>
                        </Grid>
                        {identifiers && (
                          <Grid item xs={12}>
                            <Typography variant='subtitle2' color='text.secondary'>
                              Identifiers
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {Object.entries(identifiers).map(([key, value]) => (
                                <Chip
                                  key={key}
                                  label={`${key}: ${value}`}
                                  size='small'
                                  variant='outlined'
                                />
                              ))}
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                );
              })
            ) : (
              <TextField
                value={details?.message || 'No processed steps found'}
                disabled
                multiline
                fullWidth
              />
            )}
          </Box>
        );
      } catch (error) {
        console.error('Error parsing customer webhook details:', error);
        return (
          <Paper variant='outlined' sx={{ p: 1 }}>
            <Typography variant='body2' color='error'>
              Error parsing process details
            </Typography>
          </Paper>
        );
      }
    }

    // Handle price sync recovery information
    if (selectedLog.sync_type === 'price' && selectedLog.data_synced) {
      try {
        const dataSynced = JSON.parse(selectedLog.data_synced);
        if (dataSynced.recovered) {
          return (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                  <Card sx={{
                    boxShadow: 'none',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    height: '100%'
                  }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant='subtitle2' color='text.secondary' gutterBottom>Type</Typography>
                      <Typography variant='body1' sx={{ mt: 1 }}>
                        {selectedLog.sync_type} {selectedLog.sync_sub_type && `(${selectedLog.sync_sub_type})`}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card sx={{
                    boxShadow: 'none',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    height: '100%'
                  }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant='subtitle2' color='text.secondary' gutterBottom>Direction</Typography>
                      <Typography variant='body1' sx={{ mt: 1 }}>
                        {selectedLog.direction?.split('_').join(' ').toUpperCase()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card sx={{
                    boxShadow: 'none',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    height: '100%'
                  }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant='subtitle2' color='text.secondary' gutterBottom>Status</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <StatusBadge status={selectedLog.status} size='large' />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Box sx={{ mt: 3 }}>
                <Typography variant='subtitle2' color='text.secondary'>
                  Recovery Information
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip
                    size='small'
                    label='Recovered from Interruption'
                    color='warning'
                    icon={<Box component='span' sx={{ fontSize: '16px' }}>⚠️</Box>}
                    sx={{ mr: 1, mb: 1 }}
                  />
                  <Typography variant='body2' color='text.secondary'>
                    Processed {dataSynced.recovered.lastProcessedBatch} of {dataSynced.recovered.totalBatches} batches
                    before interruption. Results shown include both successful and failed items.
                  </Typography>
                </Box>
              </Box>
            </Box>
          );
        }
      } catch (error) {
        console.error('Error parsing price sync recovery info:', error);
      }
    }

    // Handle order webhook logs (Shopify to ERP)
    if (
      selectedLog.sync_type === 'order' &&
      selectedLog.sync_sub_type === 'order_webhook' &&
      selectedLog.direction === 'shopify_to_erp'
    ) {
      try {
        const details = JSON.parse(selectedLog.details || '{}');
        const { steps, start_time, end_time, message } = details;

        return (
          <Box>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <Card sx={{
                  boxShadow: 'none',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  height: '100%'
                }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant='subtitle2' color='text.secondary' gutterBottom>Type</Typography>
                    <Typography variant='body1' sx={{ mt: 1 }}>
                      {selectedLog.sync_type} {selectedLog.sync_sub_type && `(${selectedLog.sync_sub_type})`}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{
                  boxShadow: 'none',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  height: '100%'
                }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant='subtitle2' color='text.secondary' gutterBottom>Direction</Typography>
                    <Typography variant='body1' sx={{ mt: 1 }}>
                      {selectedLog.direction?.split('_').join(' ').toUpperCase()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{
                  boxShadow: 'none',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  height: '100%'
                }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant='subtitle2' color='text.secondary' gutterBottom>Status</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <StatusBadge status={selectedLog.status} size='large' />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{
                  boxShadow: 'none',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  height: '100%'
                }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant='subtitle2' color='text.secondary' gutterBottom>Start Time</Typography>
                    <Typography variant='body1' sx={{ mt: 1 }}>
                      {formatDate(start_time)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{
                  boxShadow: 'none',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  height: '100%'
                }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant='subtitle2' color='text.secondary' gutterBottom>End Time</Typography>
                    <Typography variant='body1' sx={{ mt: 1 }}>
                      {formatDate(end_time)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{
                  boxShadow: 'none',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  height: '100%'
                }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant='subtitle2' color='text.secondary' gutterBottom>Duration</Typography>
                    <Typography variant='body1' sx={{ mt: 1 }}>
                      {formatDuration(selectedLog.duration_ms)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {message && (
              <Box sx={{ mt: 3, mb: 3 }}>
                <Typography variant='subtitle2' color='text.secondary' gutterBottom>
                  Overall Message
                </Typography>
                <Paper variant='outlined' sx={{ p: 2 }}>
                  <Typography variant='body2'>
                    {message}
                  </Typography>
                </Paper>
              </Box>
            )}

            <Typography variant='subtitle2' color='text.secondary' sx={{ mt: 2, mb: 1 }}>
              Process Steps
            </Typography>
            {steps && steps.length ? (
              steps.map((step, index) => {
                const stepKey = Object.keys(step)[0];
                const stepData = step[stepKey];
                const {
                  step_name,
                  status,
                  message: stepMessage,
                  identifiers,
                  timestamp
                } = stepData;

                return (
                  <Accordion key={index} sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ArrowRightIcon />}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%'
                        }}
                      >
                        <Typography sx={{ flex: 1 }}>
                          {step_name}
                        </Typography>
                        <StatusBadge status={status} />
                        <Typography
                          variant='caption'
                          sx={{ ml: 2, color: 'text.secondary' }}
                        >
                          {formatDate(timestamp)}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant='subtitle2' color='text.secondary'>
                            Message
                          </Typography>
                          <Typography variant='body2' sx={{ mb: 2 }}>
                            {stepMessage}
                          </Typography>
                        </Grid>
                        {identifiers && (
                          <Grid item xs={12}>
                            <Typography variant='subtitle2' color='text.secondary'>
                              Identifiers
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {Object.entries(identifiers).map(([key, value]) => (
                                <Chip
                                  key={key}
                                  label={`${key}: ${value}`}
                                  size='small'
                                  variant='outlined'
                                />
                              ))}
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                );
              })
            ) : (
              <Paper variant='outlined' sx={{ p: 2 }}>
                <Typography variant='body2' color='text.secondary'>
                  {details?.message || 'No processed steps found'}
                </Typography>
              </Paper>
            )}
          </Box>
        );
      } catch (error) {
        console.error('Error parsing order webhook details:', error);
        return (
          <Paper variant='outlined' sx={{ p: 1 }}>
            <Typography variant='body2' color='error'>
              Error parsing process details
            </Typography>
          </Paper>
        );
      }
    }

    // Handle customer sync logs (Shopify to ERP)
    if (
      selectedLog.sync_type === 'customer' &&
      selectedLog.direction === 'shopify_to_erp'
    ) {
      try {
        const details = JSON.parse(selectedLog.details || '{}');
        const { steps, start_time, end_time, message } = details;

        return (
          <Box>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <Card sx={{
                  boxShadow: 'none',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  height: '100%'
                }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant='subtitle2' color='text.secondary' gutterBottom>Type</Typography>
                    <Typography variant='body1' sx={{ mt: 1 }}>
                      {selectedLog.sync_type} {selectedLog.sync_sub_type && `(${selectedLog.sync_sub_type})`}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{
                  boxShadow: 'none',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  height: '100%'
                }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant='subtitle2' color='text.secondary' gutterBottom>Direction</Typography>
                    <Typography variant='body1' sx={{ mt: 1 }}>
                      {selectedLog.direction?.split('_').join(' ').toUpperCase()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{
                  boxShadow: 'none',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  height: '100%'
                }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant='subtitle2' color='text.secondary' gutterBottom>Status</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <StatusBadge status={selectedLog.status} size='large' />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{
                  boxShadow: 'none',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  height: '100%'
                }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant='subtitle2' color='text.secondary' gutterBottom>Start Time</Typography>
                    <Typography variant='body1' sx={{ mt: 1 }}>
                      {formatDate(start_time)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{
                  boxShadow: 'none',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  height: '100%'
                }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant='subtitle2' color='text.secondary' gutterBottom>End Time</Typography>
                    <Typography variant='body1' sx={{ mt: 1 }}>
                      {formatDate(end_time)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{
                  boxShadow: 'none',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  height: '100%'
                }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant='subtitle2' color='text.secondary' gutterBottom>Duration</Typography>
                    <Typography variant='body1' sx={{ mt: 1 }}>
                      {formatDuration(selectedLog.duration_ms)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {message && (
              <Box sx={{ mt: 3, mb: 3 }}>
                <Typography variant='subtitle2' color='text.secondary' gutterBottom>
                  Overall Message
                </Typography>
                <Paper variant='outlined' sx={{ p: 2 }}>
                  <Typography variant='body2'>
                    {message}
                  </Typography>
                </Paper>
              </Box>
            )}

            <Typography variant='subtitle2' color='text.secondary' sx={{ mt: 2, mb: 1 }}>
              Process Steps
            </Typography>
            {steps && steps.length ? (
              steps.map((step, index) => {
                const stepKey = Object.keys(step)[0];
                const stepData = step[stepKey];
                const {
                  step_name,
                  status,
                  message: stepMessage,
                  identifiers,
                  timestamp
                } = stepData;

                return (
                  <Accordion key={index} sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ArrowRightIcon />}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%'
                        }}
                      >
                        <Typography sx={{ flex: 1 }}>
                          {step_name}
                        </Typography>
                        <StatusBadge status={status} />
                        <Typography
                          variant='caption'
                          sx={{ ml: 2, color: 'text.secondary' }}
                        >
                          {formatDate(timestamp)}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant='subtitle2' color='text.secondary'>
                            Message
                          </Typography>
                          <Typography variant='body2' sx={{ mb: 2 }}>
                            {stepMessage}
                          </Typography>
                        </Grid>
                        {identifiers && (
                          <Grid item xs={12}>
                            <Typography variant='subtitle2' color='text.secondary'>
                              Identifiers
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {Object.entries(identifiers).map(([key, value]) => (
                                <Chip
                                  key={key}
                                  label={`${key}: ${value}`}
                                  size='small'
                                  variant='outlined'
                                />
                              ))}
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                );
              })
            ) : (
              <Paper variant='outlined' sx={{ p: 2 }}>
                <Typography variant='body2' color='text.secondary'>
                  {details?.message || 'No processed steps found'}
                </Typography>
              </Paper>
            )}
          </Box>
        );
      } catch (error) {
        console.error('Error parsing customer sync details:', error);
        return (
          <Paper variant='outlined' sx={{ p: 1 }}>
            <Typography variant='body2' color='error'>
              Error parsing process details
            </Typography>
          </Paper>
        );
      }
    }

    // Continue with the default dialog content
    return (
      <Box>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Card sx={{
              boxShadow: 'none',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              height: '100%'
            }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant='subtitle2' color='text.secondary' gutterBottom>Type</Typography>
                <Typography variant='body1' sx={{ mt: 1 }}>
                  {selectedLog.sync_type} {selectedLog.sync_sub_type && `(${selectedLog.sync_sub_type})`}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{
              boxShadow: 'none',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              height: '100%'
            }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant='subtitle2' color='text.secondary' gutterBottom>Direction</Typography>
                <Typography variant='body1' sx={{ mt: 1 }}>
                  {selectedLog.direction?.split('_').join(' ').toUpperCase()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{
              boxShadow: 'none',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              height: '100%'
            }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant='subtitle2' color='text.secondary' gutterBottom>Status</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <StatusBadge status={selectedLog.status} size='large' />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{
              boxShadow: 'none',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              height: '100%'
            }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant='subtitle2' color='text.secondary' gutterBottom>Initiated By</Typography>
                <Typography variant='body1' sx={{ mt: 1 }}>
                  {selectedLog.initiated_by?.toUpperCase()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{
              boxShadow: 'none',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              height: '100%'
            }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant='subtitle2' color='text.secondary' gutterBottom>Timestamp</Typography>
                <Typography variant='body1' sx={{ mt: 1 }}>
                  {formatDate(selectedLog.created_at)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{
              boxShadow: 'none',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              height: '100%'
            }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant='subtitle2' color='text.secondary' gutterBottom>Duration</Typography>
                <Typography variant='body1' sx={{ mt: 1 }}>
                  {formatDuration(selectedLog.duration_ms)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {selectedLog.error_message && (
          <Box sx={{ mb: 3 }}>
            <Typography variant='subtitle2' color='text.secondary' gutterBottom>
              Error Details
            </Typography>
            <Paper
              variant='contained'
              color='error'
              sx={{
                p: 2,
                backgroundColor: 'error.main',
                borderColor: 'error.main',
                borderRadius: 2,
                '& pre': {
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  color: 'white',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  lineHeight: 1.5
                }
              }}
            >
              <pre>{selectedLog.error_message}</pre>
            </Paper>
          </Box>
        )}

        <>
          {renderFilterChips()}

          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2,
            flexDirection: 'column',
            gap: 2,
            width: '100%'
          }}>
            <Typography variant='subtitle2' color='text.secondary'>Synced Items</Typography>
            <form onSubmit={handleSearchSubmit} style={{ margin: 0, width: '100%' }}>
              <TextField
                size='small'
                placeholder={selectedLog.sync_type === 'price' ? 'Search rules, customers, items...' : 'Search items...'}
                value={itemSearchTerm}
                onChange={handleItemSearch}
                sx={{
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'background.paper',
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main'
                      }
                    }
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon color='action' />
                    </InputAdornment>
                  ),
                  endAdornment: itemSearchTerm && (
                    <InputAdornment position='end'>
                      <IconButton
                        size='small'
                        onClick={() => {
                          setItemSearchTerm('');
                          if (selectedLog && detailedData !== null) {
                            fetchDetailedData(selectedLog.id, 0, itemsPerPage, currentFilter);
                          }
                        }}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'action.hover'
                          }
                        }}
                      >
                        <CloseIcon fontSize='small' />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </form>
          </Box>

          {renderSyncItems()}
        </>
      </Box>
    );
  };

  return (
    <Box>
      <PageHeader
        title='Sync Logs'
        subtitle='View and filter synchronization logs'
        responsiveDirectionColumn={true}
        buttons={
          <Box display='flex' gap={2}>
            <Badge
              color='secondary'
              badgeContent={getActiveFilterCount(filters) > 0 ? getActiveFilterCount(filters) : null}
              overlap='circular'
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Button
                variant='contained'
                color='primary'
                startIcon={<FilterIcon />}
                onClick={() => setShowFilters(!showFilters)}
                disabled={isFetching || isLoading}
              >
                Filters
              </Button>
            </Badge>
            <Button
              variant='contained'
              color='primary'
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
              disabled={isFetching || isLoading}
            >
              Refresh
            </Button>
          </Box>
        }
      />

      <Dialog open={showFilters} onClose={() => setShowFilters(false)} maxWidth='md' fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 2 }}>
          Filters
          <IconButton onClick={() => setShowFilters(false)} size='small' sx={{ ml: 2 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ pt: 1 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                name='syncType'
                label='Sync Type'
                value={modalFilters.syncType}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value=''>All</MenuItem>
                <MenuItem value='customer'>Customer</MenuItem>
                <MenuItem value='product'>Product</MenuItem>
                <MenuItem value='price'>Price</MenuItem>
                <MenuItem value='order'>Order</MenuItem>
                <MenuItem value='tracking'>Tracking</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                name='direction'
                label='Direction'
                value={modalFilters.direction}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value=''>All</MenuItem>
                <MenuItem value='erp_to_shopify'>ERP to Shopify</MenuItem>
                <MenuItem value='shopify_to_erp'>Shopify to ERP</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                name='status'
                label='Status'
                value={modalFilters.status}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value=''>All</MenuItem>
                <MenuItem value='success'>Success</MenuItem>
                <MenuItem value='partial_success'>Partial Success</MenuItem>
                <MenuItem value='failed'>Failed</MenuItem>
                <MenuItem value='in_progress'>In Progress</MenuItem>
                <MenuItem value='in_queue'>In Queue</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                name='initiatedBy'
                label='Initiated By'
                value={modalFilters.initiatedBy}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value=''>All</MenuItem>
                <MenuItem value='manual'>Manual</MenuItem>
                <MenuItem value='scheduler'>Scheduler</MenuItem>
                <MenuItem value='webhook'>Webhook</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type='date'
                name='startDate'
                label='Start Date'
                value={modalFilters.startDate}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type='date'
                name='endDate'
                label='End Date'
                value={modalFilters.endDate}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            variant='contained'
            color='secondary'
            onClick={() => { resetFilters(); setShowFilters(false); }}
            disabled={!hasAppliedFilters()}
          >
            Clear Filters
          </Button>
          <Button
            variant='contained'
            onClick={() => { applyFilters(); setShowFilters(false); }}
            disabled={!hasFilterFormChanges()}
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      <ServerDataTable
        columns={columns}
        currentPage={page}
        data={data?.logs || []}
        totalRows={data?.pagination?.total || 0}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handlePageSizeChange}
        rowsPerPageOptions={[10, 25, 50, 100]}
        noDataMessage='No sync logs found'
        pageSize={pageSize}
        loading={isLoading || isFetching}
        totalPages={Math.ceil((data?.pagination?.total || 0) / pageSize)}
        rowsPerPage={pageSize}
      />

      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        maxWidth='lg'
        fullWidth={true}
        fullScreen={isDialogMaximized}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 2
        }}>
          <Typography variant='h6' component='div' sx={{ fontWeight: 600 }}>Sync Log Details : {selectedLog?.id}</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              size='small'
              onClick={() => toggleDialogSize()}
              sx={{
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              {isDialogMaximized ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
            <IconButton
              size='small'
              onClick={handleCloseDialog}
              sx={{
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {renderDialogContent()}
        </DialogContent>

        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button onClick={handleCloseDialog} variant='contained' color='secondary'>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
